import * as yup from 'yup';
import { Form, Formik, FormikHelpers } from 'formik';
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { CountryCode } from '../../generated/graphql';
import { FormTextField } from '../../ui';
import { PendingAccount } from './types';

export type PendingAccountFormValues = PendingAccount;

export interface PendingAccountFormProps {
  initialValues: PendingAccountFormValues;
  onSubmit: (values: PendingAccountFormValues) => void;
  showAutoCategorizationToggle?: boolean;
}

const pendingAccountFormValidation = yup.object().shape({
  email: yup.string().email().required(),
  firstName: yup.string().required(),
  lastName: yup.string().required(),
  venueName: yup.string().required(),
  venueCountry: yup.string().required(),
  crmLink: yup.string().when('isTest', {
    is: true,
    then: yup.string().url('Must be a valid URL'),
    otherwise: yup
      .string()
      .url('Must be a valid URL')
      .required('CRM link is required for non-test venues'),
  }),
});

export const PendingAccountForm: React.FC<PendingAccountFormProps> = ({
  initialValues,
  onSubmit,
  showAutoCategorizationToggle = false,
}) => {
  const theme = useTheme();

  const handleSubmit = (
    values: PendingAccountFormValues,
    { resetForm }: FormikHelpers<PendingAccountFormValues>,
  ) => {
    onSubmit(values);
    resetForm();
  };

  return (
    <Formik<PendingAccountFormValues>
      initialValues={initialValues}
      validationSchema={pendingAccountFormValidation}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, setFieldValue, values }) => (
        <Form>
          <Box display="flex" flexDirection="column" gap={0}>
            <FormTextField
              required
              id="venueName"
              name="venueName"
              label="Venue Name"
            />
            <FormTextField required id="email" name="email" label="Email" />
            <Box display="flex" gap={2}>
              <FormTextField
                required
                id="firstName"
                name="firstName"
                label="First name"
              />
              <FormTextField
                required
                id="lastName"
                name="lastName"
                label="Last name"
              />
            </Box>
            <FormTextField
              select
              required
              id="venueCountry"
              name="venueCountry"
              label="Country"
            >
              {[
                CountryCode.CzechRepublic,
                CountryCode.Slovakia,
                CountryCode.Poland,
                CountryCode.UnitedKingdom,
                CountryCode.UnitedStates,
                CountryCode.Denmark,
                CountryCode.Spain,
                CountryCode.Sweden,
                CountryCode.Norway,
                CountryCode.Ireland,
              ].map((country) => (
                <MenuItem
                  key={country}
                  value={country}
                  onClick={() => setFieldValue('venueCountry', country)}
                >
                  {country}
                </MenuItem>
              ))}
            </FormTextField>
            <FormTextField
              required={!values.isTest}
              id="crmLink"
              name="crmLink"
              label={
                values.isTest
                  ? 'CRM Link (optional for test venues)'
                  : 'CRM Link (e.g. Pipedrive deal URL)'
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={values.isTest}
                  onChange={(e) => setFieldValue('isTest', e.target.checked)}
                />
              }
              label="Test Venue"
            />
            {showAutoCategorizationToggle && (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!values.useAutoCategorization}
                    onChange={(e) =>
                      setFieldValue('useAutoCategorization', e.target.checked)
                    }
                  />
                }
                label="Enable autocategorization"
              />
            )}
            <Typography color={theme.palette.text.secondary} mt={2}>
              This form will immediately create the provided venue, but it will
              not yet send the login creadentials to the user. Instead, it will
              be placed into the Pending list on the right where you can Send,
              Edit, or Delete the new account later.
            </Typography>
            <Box display="flex" justifyContent="flex-end">
              <Button disabled={isSubmitting} variant="contained" type="submit">
                Confirm
              </Button>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
};
